import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Trash2, Star as StarIcon, Download, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [formData, setFormData] = useState({
    guest_name: '',
    rating: 5,
    review_text: '',
    review_date: new Date().toISOString().split('T')[0],
    source: 'manual',
  });

  useEffect(() => {
    fetchReviews();
    fetchGoogleStatus();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      if (response.data.success) {
        setReviews(response.data.reviews);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchGoogleStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/google-reviews-status`);
      setGoogleStatus(response.data);
    } catch (error) {
      console.error('Error fetching Google status:', error);
    }
  };

  const handleImportGoogleReviews = async () => {
    setImporting(true);
    try {
      const response = await axios.post(`${API}/admin/import-google-reviews`);
      if (response.data.success) {
        toast.success(`Imported ${response.data.imported} new reviews!`);
        fetchReviews();
        fetchGoogleStatus();
      }
    } catch (error) {
      console.error('Error importing Google reviews:', error);
      toast.error(error.response?.data?.detail || 'Failed to import Google reviews');
    } finally {
      setImporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API}/reviews`, {
        ...formData,
        rating: parseInt(formData.rating),
      });
      toast.success('Review added successfully!');
      setDialogOpen(false);
      resetForm();
      fetchReviews();
    } catch (error) {
      console.error('Error creating review:', error);
      toast.error('Failed to add review');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      await axios.delete(`${API}/reviews/${reviewId}`);
      toast.success('Review deleted successfully');
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Failed to delete review');
    }
  };

  const resetForm = () => {
    setFormData({
      guest_name: '',
      rating: 5,
      review_text: '',
      review_date: new Date().toISOString().split('T')[0],
      source: 'manual',
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon
            key={star}
            size={16}
            className={star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-12">Loading reviews...</div>;
  }

  return (
    <div className="space-y-6" data-testid="reviews-container">
      <Toaster />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Reviews</h1>
          <p className="text-gray-600 mt-1">Manage guest reviews and testimonials</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700" data-testid="add-review-button">
              <Plus className="mr-2" size={16} />
              Add Review
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Review</DialogTitle>
              <DialogDescription>Add a guest review or import from Google/Airbnb</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="guest_name">Guest Name *</Label>
                <Input
                  id="guest_name"
                  value={formData.guest_name}
                  onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                  required
                  data-testid="review-guest-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rating">Rating *</Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="rating"
                    type="number"
                    min="1"
                    max="5"
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                    required
                    className="w-20"
                    data-testid="review-rating-input"
                  />
                  {renderStars(parseInt(formData.rating))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="review_text">Review Text *</Label>
                <Textarea
                  id="review_text"
                  value={formData.review_text}
                  onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                  rows={4}
                  required
                  data-testid="review-text-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="review_date">Review Date *</Label>
                <Input
                  id="review_date"
                  type="date"
                  value={formData.review_date}
                  onChange={(e) => setFormData({ ...formData, review_date: e.target.value })}
                  required
                  data-testid="review-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="source">Source</Label>
                <select
                  id="source"
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                  data-testid="review-source-select"
                >
                  <option value="manual">Manual Entry</option>
                  <option value="google">Google</option>
                  <option value="airbnb">Airbnb</option>
                  <option value="booking.com">Booking.com</option>
                </select>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="submit-review-button">
                  Add Review
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Reviews ({reviews.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {reviews.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <StarIcon className="mx-auto mb-4" size={48} />
              <p>No reviews yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  data-testid={`review-item-${review.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{review.guest_name}</h3>
                        {renderStars(review.rating)}
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                          {review.source}
                        </span>
                      </div>
                      <p className="text-gray-700 mb-2">{review.review_text}</p>
                      <p className="text-sm text-gray-500">Date: {review.review_date}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(review.id)}
                      data-testid={`delete-review-${review.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-r from-blue-50 to-green-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
            Google Reviews Import
            {googleStatus?.auto_sync_enabled && (
              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                <CheckCircle size={12} />
                Auto-Sync ON
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {googleStatus?.configured ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
                <div>
                  <p className="font-semibold text-gray-800">{googleStatus.place_name || 'Himali Khim Homestay'}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex">{renderStars(Math.round(googleStatus.google_rating || 5))}</div>
                    <span className="text-sm text-gray-600">
                      {googleStatus.google_rating} ({googleStatus.total_google_reviews} reviews on Google)
                    </span>
                  </div>
                  {googleStatus.last_sync && (
                    <p className="text-xs text-gray-500 mt-1">
                      Last synced: {new Date(googleStatus.last_sync).toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Imported to website:</p>
                  <p className="text-2xl font-bold text-green-600">{googleStatus.imported_reviews || 0}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Button 
                  onClick={handleImportGoogleReviews}
                  disabled={importing}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="import-google-reviews-button"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="mr-2 animate-spin" size={16} />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Download className="mr-2" size={16} />
                      Import Now
                    </>
                  )}
                </Button>
                <span className="text-sm text-gray-600">
                  Or wait for automatic daily sync at 6:00 AM IST
                </span>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <CheckCircle className="inline mr-1" size={14} />
                  <strong>Auto-Sync Enabled:</strong> New Google reviews are automatically imported daily at 6:00 AM IST.
                </p>
              </div>
              
              <p className="text-xs text-gray-500">
                Note: Google API returns up to 5 most recent reviews per sync. Reviews are automatically de-duplicated.
              </p>
            </div>
          ) : (
            <div className="text-sm text-blue-800">
              <p className="mb-2">Google Reviews integration is not configured.</p>
              <p>Contact support to set up automatic Google Reviews import.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Reviews;